import { ErrorMessage } from '@hookform/error-message';
import { useGetQuestionnaireByIdQueryState } from 'app/services/questionnaires';
import React, { useEffect } from 'react';
import {
  FieldValues,
  RegisterOptions,
  useFormContext,
  UseFormReturn,
} from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { getLabel } from 'utils';
import renderInput from 'utils/renderInput';
import { useFieldArrayItemName } from '../FieldArrayInput';
import Label from '../Label';

export interface IBaseInputProps extends IBaseInput {
  name: string;
  labelIcon?: React.ReactNode;
  children: (props: UseFormReturn<FieldValues, object>) => JSX.Element;
  rules?: RegisterOptions;
  effect?: (payload: { key: string; value: any }) => void;
  updateRemoteKey?: () => void;
}

interface IFieldLabel {
  label?: string | (() => React.ReactNode);
  labelIcon?: React.ReactNode;
  subLabel?: () => React.ReactNode;
}

export function FieldLabel(props: IFieldLabel) {
  const { label, labelIcon, subLabel } = props;

  const methods = useFormContext();
  const { questionnaireId } = useParams();
  const { data: questionnaireData } = useGetQuestionnaireByIdQueryState(
    questionnaireId!,
  );
  const questionnaireYear = questionnaireData?.questionnaire.year;
  const { getValues } = methods;
  const labelTransformed = getLabel(getValues, label, questionnaireYear);

  return (
    <>
      {label && (
        <div className="inline-flex items-center w-full">
          {labelIcon && <span className="pr-2 mb-1">{labelIcon}</span>}
          <Label label={labelTransformed || ''} className="w-full" />
        </div>
      )}
      {subLabel && (
        <div className="text-gray-lighter text-xs mb-3 flex justify-between items-center w-full">
          {subLabel()}
        </div>
      )}
    </>
  );
}

export default function BaseInput(props: IBaseInputProps) {
  const {
    name,
    label,
    subLabel,
    children,
    dependencies,
    effect,
    updateRemoteKey,
    dependenciesTitle,
  } = props;

  const methods = useFormContext();
  const {
    formState: { errors },
    getValues,
    watch,
  } = methods;
  const nameWatched = watch(name);

  useEffect(
    () => {
      // if (effect && getValues(name) !== null && getValues(name) !== undefined) {
      effect && effect({ key: name, value: getValues(name) });
      // }
      if (updateRemoteKey) {
        updateRemoteKey();
      }
    },
    effect ? [nameWatched] : [],
  );

  return (
    <>
      <div>
        {dependenciesTitle && (
          <h3 className="font-semibold mt-4 text-gray-dark">
            {dependenciesTitle}
          </h3>
        )}
        <FieldLabel {...props} label={label} subLabel={subLabel} />
        {children({ ...methods })}
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
              {message}
            </p>
          )}
        />
      </div>

      <DependenciesList fieldValue={nameWatched} dependencies={dependencies} />
    </>
  );
}

function DependenciesList({
  fieldValue,
  dependencies,
}: {
  fieldValue: any;
  dependencies?: IBaseField<any>[];
}) {
  return (
    <>
      {fieldValue !== null &&
        fieldValue !== undefined &&
        fieldValue !== '' &&
        dependencies &&
        dependencies.length > 0 &&
        dependencies
          .filter((dep) => (dep.predicate ? dep.predicate(fieldValue) : true))
          .map((dep, j) => <RenderDependency key={dep.name + j} field={dep} />)}
    </>
  );
}

export function RenderDependency({ field }: { field: IBaseField<any> }) {
  const fieldName = useFieldArrayItemName(field.name);
  return (
    <div className="border-gray-800 border-l p-3 ml-6 ">
      <div key={field.name} className="ml-6 mt-2">
        <RenderInput field={field} />
      </div>
    </div>
  );
}

export function RenderInput({ field }: { field: IBaseField<any> }) {
  const fieldName = useFieldArrayItemName(field.name);

  return <>{renderInput({ ...field, name: fieldName })}</>;
}
